import { ChangeDetectorRef, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { ADDRESS_FIELDS, Address } from '@df/session/api/address-book/address';
import { AddressBook } from '@df/session/api/address-book/address-book';
import findKey from 'lodash-es/findKey';
import pick from 'lodash-es/pick';
import { Subscription } from 'rxjs';
import { NCheckout } from './df-checkout.config';

@Directive()
export abstract class CheckoutAddresssComponentDef implements OnInit, OnDestroy {
    protected app = inject(App);

    private moduleConfig = inject(NCheckout.OPTIONS);

    public readonly checkoutFormComponent = inject(CheckoutFormComponentDef);

    public readonly checkoutStepComponent = inject(CheckoutStepComponentDef);

    readonly addressBook = inject(AddressBook);

    private changeDetectorRef = inject(ChangeDetectorRef);

    private timeoutService = inject(TimeoutService);

    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.onStepFormSet(form);
    }

    /**
     * Set for billing address
     */
    @Input()
    isBilling = false;

    /**
     * Override for standard payment data object
     */
    protected _data!: Checkout.IAddress;

    /**
     * Indictes if component was already initiated
     */
    protected initiated = false;

    @Output()
    dataChange = new EventEmitter<Checkout.IAddress>();

    private dataChangeSubscription?: Subscription;

    /**
     * Address data setter
     */
    @Input({
        required: true
    })
    set data(data: Checkout.IAddress) {
        this._data = data;
        if (this.initiated) {
            this.checkData();
        }
    }

    /**
     * Address data getter
     */
    get data(): Checkout.IAddress {
        return this._data;
    }

    protected subscriptions = new Subscription();

    protected onStepFormSet(form: NgForm): void {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
        this.dataChangeSubscription?.unsubscribe();
        this.dataChangeSubscription = form?.valueChanges!.subscribe(() => this.dataChange.emit(this.data));
    }

    /////////////////////////////////////////////
    //
    // EVENTS
    //
    /////////////////////////////////////////////

    /**
     * Set up component
     */
    ngOnInit() {
        const checkDebounced = this.timeoutService.debounce(() => this.checkData(), 100);
        this.subscriptions.add(this.addressBook.subscribe(checkDebounced));
        this.subscriptions.add(this.app.customer.model.obs.status.subscribe(checkDebounced));
        this.checkData();
        this.initiated = true;
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.dataChangeSubscription?.unsubscribe();
    }

    /////////////////////////////////////////////
    //
    // COMPONENT DATA
    //
    /////////////////////////////////////////////

    /**
     * pre select default address if empty address selected
     */
    protected checkData() {
        if (!this.app.customer.model.isLoggedIn && this.data.customer_address_id) {
            delete this.data.customer_address_id;
        }

        if (this.data && this.data.customer_address_id === undefined) {
            let defaultAddress = this.isBilling ? this.addressBook.billing : this.addressBook.shipping;
            if (defaultAddress && !this.canUseAddress(defaultAddress)) {
                defaultAddress = undefined;
            }
            if (!defaultAddress && this.moduleConfig.autoPickAnyAddress) {
                defaultAddress = this.addressBook.addresses[0];
            }
            if (defaultAddress && !this.canUseAddress(defaultAddress)) {
                defaultAddress = undefined;
            }
            if (this.canSwitchToDefaultAddress() && defaultAddress) {
                // if there are no other non empty fields then specified in config
                this.applyAddress(defaultAddress);
            } else {
                if (this.isBilling) {
                    this.data.customer_address_id =
                        this.checkoutFormComponent.isBillingToShipping && this.checkoutFormComponent.hasDeliveryAddress ? undefined : 0;
                } else {
                    this.data.customer_address_id = 0;
                }
            }
        }

        this.detectChanges();
    }

    /**
     * If address is given then its data will be applied to the component data
     * If address is not defined then form will be reset
     */
    applyAddress(address?: Address): void {
        if (address) {
            Object.assign(this.data, pick(address.data, ADDRESS_FIELDS));
            this.data.customer_address_id = address.id;
        } else {
            ADDRESS_FIELDS.forEach(field => {
                if (field !== 'country' && field !== 'country_id') {
                    this.data[field] = undefined;
                }
            });
            this.data.customer_address_id = 0;
        }
        this.detectChanges();
    }

    onAddressIdChange() {
        this.applyAddress(this.data.customer_address_id ? this.addressBook.byId(this.data.customer_address_id) : undefined);
    }

    protected canSwitchToDefaultAddress() {
        // need to be logged in
        if (!this.app.customer.model.isLoggedIn) {
            return false;
        }
        // for billing address we cannot set if is billing to shipping
        if (this.isBilling && this.checkoutFormComponent.data.billing.to_shipping) {
            return false;
        }
        // if address is not empty
        if (findKey(this.data, (value, key) => value && !this.moduleConfig.fieldsNotPreventingDefaultAddress.includes(key))) {
            return false;
        }
        // otherwise good to change
        return true;
    }

    /**
     * Checks if address list should be shwon
     * @return {boolean}
     */
    get shouldShowList() {
        // yes if there is billing address (as there will be billing to shipping too),
        // so we need to show 'other' (as long as its not collection method)
        if (this.isBilling && this.checkoutFormComponent.hasDeliveryAddress) {
            return true;
        }
        // yes if there are addresses
        if (this.addresses.length) {
            return true;
        }

        return false;
    }

    // function this.isBilling {
    //     return this.type == 'billing';
    // }

    /**
     * Checks if we should present part that is responsible for new data
     */
    get shouldShowNotToShippingPart() {
        if (this.checkoutFormComponent.isBillingToShipping && this.checkoutFormComponent.hasDeliveryAddress && this.isBilling) {
            return false;
        }
        return true;
    }

    get shouldShowAddressBook(): boolean {
        return !this.checkoutFormComponent.isGuestCheckout && this.addresses.length > 0;
    }

    /**
     * Checks if address list should be shwon
     *
     * IMPORTANT: use it only if to_shipping box is checkbox. If its radio then you should always show this option.
     *
     * @return {boolean}
     */
    get shouldShowAddNew() {
        // we only show this option if there are other adresses to pick
        return this.addresses?.length > 0;
    }

    /**
     * Checks if address list should be shwon
     * @return {boolean}
     */
    get shouldShowForm() {
        // do not show if its billing to shipping
        if (this.isBilling && this.checkoutFormComponent.isBillingToShipping && this.checkoutFormComponent.hasDeliveryAddress) {
            return false;
        }

        // yes if current option is 'other'
        if (!this.data.customer_address_id) {
            // force '0' value if undefined
            this.data.customer_address_id = 0;
            return true;
        }
        // yes if there are no addresses and address is NOT billing as billing has also 'ship to billing'
        // option allowed (if not collection type method is picked)
        if (!this.addresses.length && (!this.isBilling || !this.checkoutFormComponent.hasDeliveryAddress)) {
            return true;
        }
        return false;
    }

    /**
     * Checks if billing to shipping option should be visible
     * @return {boolean}
     */
    get shouldShowToShipping() {
        // not if its not billing address
        if (!this.isBilling) {
            return false;
        }
        // not quote is virtual then no
        // if(this.type != 'billing') {
        //     return false;
        // }
        // not if checkout is virtual, no if collection method is picked
        if (!this.checkoutFormComponent.hasDeliveryAddress) {
            return false;
        }
        return true;
    }

    get addresses() {
        return (this.isBilling ? this.addressBook.addresses : this.addressBook.shippingAddresses).filter(i => this.canUseAddress(i));
    }

    protected canUseAddress(address: Address) {
        return true;
    }

    // we don't need to do anything, we need on searcher selection applied to fire ngDoCheck in checkout form component
    onSearcherSelectionApplied(): void {}

    addressTrackFn(index, address: Address) {
        return address.id;
    }

    save(): void {
        if (this.isBilling) {
            this.checkoutFormComponent.saveBillingAddress();
        } else {
            this.checkoutFormComponent.saveShippingAddress();
        }
    }

    detectChanges() {
        this.changeDetectorRef.detectChanges();
    }
}
